/**
 * Every chat request in one place: the CSRF header, the conversation id, JSON parsing and one
 * error shape. A component never builds a fetch of its own, so a new endpoint can't quietly
 * forget the token or the "read the message out of the error body" step.
 */
export type ChatApiConfig = {
    csrfToken: string;
    conversationId: string;
    sendUrl: string;
    resetUrl: string;
    verifyReturnUrl: string;
    submitReturnUrl: string;
    verifyOrderStatusUrl: string;
};

export type ReturnLine = { id: number; reasonCode: number | null; motivation: string };
export type ActiveFilter = { facetKey: string; label: string; values: string[] };

export function createChatApi(config: ChatApiConfig) {
    async function post(url: string, body: Record<string, unknown>, fallbackMessage: string) {
        let response: Response;
        try {
            response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': config.csrfToken,
                    Accept: 'application/json',
                },
                body: JSON.stringify({ conversationId: config.conversationId, ...body }),
            });
        } catch (error) {
            throw new Error('Geen verbinding met de server. Probeer het opnieuw.');
        }

        let payload: any = {};
        try {
            payload = await response.json();
        } catch (error) {
            payload = {};
        }

        if (!response.ok) {
            throw new Error(payload.message || fallbackMessage);
        }

        return payload;
    }

    return {
        send: (message: string, choiceId?: string | null) =>
            post(config.sendUrl, choiceId ? { message, choiceId } : { message }, 'Er is een fout opgetreden.'),
        removeFilter: (filter: ActiveFilter) => post(config.sendUrl, {
            message: `Verwijder filter ${filter.label}: ${filter.values.join(', ')}`,
            filterAction: 'remove',
            facetKey: filter.facetKey,
        }, 'De filter kon niet worden verwijderd.'),
        reset: () => post(config.resetUrl, {}, 'Resetten is mislukt.'),
        verifyReturn: (orderId: string, emailAddress: string) =>
            post(config.verifyReturnUrl, { orderId, emailAddress }, 'Er is een fout opgetreden.'),
        submitReturn: (orderLines: ReturnLine[]) =>
            post(config.submitReturnUrl, { orderLines }, 'Er is een fout opgetreden.'),
        verifyOrderStatus: (orderId: string, emailAddress: string) =>
            post(config.verifyOrderStatusUrl, { orderId, emailAddress }, 'Er is een fout opgetreden.'),
    };
}
